import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import {
  type Customer,
  type CustomerDefaultUtility,
  type CustomerListResponse,
  type CustomerWizardPayload,
  type CrmStatus,
  type HubspotSearchPayload,
  type HubspotSearchResponse,
  type Industry,
  type RelinkCustomerPayload,
  type UpdateCustomerPayload,
  isCrmSyncing,
} from "@/types/customers";
import type { Commodity } from "@/types/generated/Commodity";

/** Root key for all customer queries (list + detail). Exported so mutations in
 *  other domains (e.g. deal transitions that advance a customer's lifecycle)
 *  can invalidate the whole customer cache. */
export const CUSTOMERS_KEY = ["customers"] as const;
export const customerKey = (id: string) => ["customers", id] as const;

// ── List ──────────────────────────────────────────────────────────────────────

export interface CustomersListParams {
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
  page?: number;
  per_page?: number;
  /** AB#7774 chip filters — each a comma-separated "is any of" list. */
  lifecycle_state?: string;
  agent_id?: string;
  crm_link_state?: string;
  industry?: string;
  state?: string;
  /** Inclusive `updated_at` day bounds — ISO `yyyy-MM-dd` or a relative
   *  token (`today`, `today±Nd`), resolved server-side. */
  updated_from?: string;
  updated_to?: string;
}

export function useCustomers(params: CustomersListParams = {}) {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.sort) qs.set("sort", params.sort);
  if (params.dir) qs.set("dir", params.dir);
  if (params.page) qs.set("page", String(params.page));
  if (params.per_page) qs.set("per_page", String(params.per_page));
  if (params.lifecycle_state) qs.set("lifecycle_state", params.lifecycle_state);
  if (params.agent_id) qs.set("agent_id", params.agent_id);
  if (params.crm_link_state) qs.set("crm_link_state", params.crm_link_state);
  if (params.industry) qs.set("industry", params.industry);
  if (params.state) qs.set("state", params.state);
  if (params.updated_from) qs.set("updated_from", params.updated_from);
  if (params.updated_to) qs.set("updated_to", params.updated_to);
  const path = `/api/customers${qs.toString() ? `?${qs.toString()}` : ""}`;

  return useQuery({
    queryKey: [...CUSTOMERS_KEY, params],
    queryFn: () => api.get<CustomerListResponse>(path),
    // While a row's HubSpot sync is in flight (the list's coarse `syncing`
    // code), refetch until it settles, so the HubSpot column never stays on
    // "Syncing" after the sync has finished. Same cadence as `useCustomer`.
    refetchInterval: (query) =>
      query.state.data?.items.some((c) => c.crm_link_state === "syncing") ? 3000 : false,
  });
}

// ── Detail (with conditional polling while HubSpot sync is in flight) ─────────

/**
 * Loads a customer. When the customer is in a pending/syncing CRM state,
 * React Query refetches every 3 seconds until it flips to a terminal state
 * (linked, sync_failed, unlinked). Polling also pauses automatically when
 * the browser tab is hidden — React Query's default. This matches the
 * pattern modern admin dashboards use: active = poll, terminal = stop,
 * hidden = pause.
 *
 * `staleTime: 0` overrides the global 30s default so navigating back to a
 * detail page always refetches on mount. The customer's state changes out of
 * band (deal transitions advance lifecycle, HubSpot sync, edits elsewhere), so
 * the cached value is shown instantly but replaced by a fresh fetch on every
 * visit — otherwise a badge can read stale for up to 30s after a DB change.
 */
export function useCustomer(id: string | undefined) {
  return useQuery({
    queryKey: id ? customerKey(id) : ["customers", "no-id"],
    queryFn: () => api.get<Customer>(`/api/customers/${id}`),
    enabled: !!id,
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      // Poll while EITHER the company-field link or the lifecycle-stage push
      // is mid-sync, so both badges settle without a manual refresh.
      const syncing =
        isCrmSyncing(data.crm_link_state) ||
        isCrmSyncing(data.lifecycle_sync_state);
      return syncing ? 3000 : false;
    },
  });
}

/**
 * Loads a customer purely to resolve its display name — for a picker trigger,
 * a breadcrumb, or anywhere the CRM badges aren't rendered.
 *
 * Same cache entry as `useCustomer` (so no duplicate fetch when a detail page
 * is also mounted), but deliberately without its `staleTime: 0` +
 * `refetchInterval` polling: those exist so sync badges settle, and a caller
 * showing only a name has no badge to settle. Since `isCrmSyncing` stays true
 * until the outbox drains, borrowing `useCustomer` for a label meant a stuck
 * job polled `/api/customers/:id` every 3s indefinitely from an unrelated page
 * (AB#7741). React Query observer options are per-hook, so this caller stays
 * quiet even while a detail page polls the same key.
 */
export function useCustomerLabel(id: string | undefined) {
  return useQuery({
    queryKey: id ? customerKey(id) : ["customers", "no-id"],
    queryFn: () => api.get<Customer>(`/api/customers/${id}`),
    enabled: !!id,
    staleTime: 5 * 60_000,
  });
}

// ── Industries ───────────────────────────────────────────────────────────────

export function useIndustries() {
  return useQuery({
    queryKey: ["customers", "industries"],
    queryFn: () => api.get<Industry[]>("/api/customers/industries"),
    // Industries are effectively static — cache for a long time.
    staleTime: 60 * 60_000,
  });
}

// ── HubSpot Company search (wizard step 2) ────────────────────────────────────

export function useHubspotCompanySearch() {
  // Not idempotent — each call is a fresh search. Use a mutation so the
  // wizard can trigger it imperatively (await the result, render matches).
  return useMutation({
    mutationFn: (body: HubspotSearchPayload) =>
      api.post<HubspotSearchResponse>("/api/customers/hubspot-search", body),
  });
}

// ── Create (wizard step 3) ───────────────────────────────────────────────────

export function useCreateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CustomerWizardPayload) =>
      api.post<{ id: string }>("/api/customers", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: CUSTOMERS_KEY }),
  });
}

// ── Update ───────────────────────────────────────────────────────────────────

export function useUpdateCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCustomerPayload }) =>
      api.put<void>(`/api/customers/${id}`, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      qc.invalidateQueries({ queryKey: customerKey(id) });
    },
  });
}

// ── Relink ───────────────────────────────────────────────────────────────────

export function useRelinkCustomer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: RelinkCustomerPayload }) =>
      api.post<void>(`/api/customers/${id}/relink`, body),
    onSuccess: (_data, { id }) => {
      qc.invalidateQueries({ queryKey: CUSTOMERS_KEY });
      qc.invalidateQueries({ queryKey: customerKey(id) });
    },
  });
}

// ── Default utility (deal-name auto-fill) ─────────────────────────────────────

/**
 * Resolves the unambiguous utility short_name for a customer in a given
 * state, optionally narrowed to a commodity. Used by the deal-create form
 * to auto-fill the utility token in the deal name. Returns
 * `{ short_name: null }` when zero or multiple utilities match — the
 * caller treats null as "not deducible, omit the token".
 */
export function useDefaultUtility(
  customerId: string | null | undefined,
  state: string | null | undefined,
  commodity: Commodity | null | undefined,
) {
  const enabled = !!customerId && !!state;
  const qs = new URLSearchParams();
  if (state) qs.set("state", state);
  if (commodity) qs.set("commodity", commodity);
  return useQuery({
    queryKey: ["customers", customerId, "default-utility", state, commodity],
    queryFn: () =>
      api.get<CustomerDefaultUtility>(
        `/api/customers/${customerId}/default-utility?${qs.toString()}`,
      ),
    enabled,
  });
}

// ── CRM status poll (used internally by useCustomer; exposed for manual use) ──

export function useCrmStatus(id: string | undefined) {
  return useQuery({
    queryKey: id ? ["customers", id, "crm-status"] : ["customers", "no-id", "crm-status"],
    queryFn: () => api.get<CrmStatus>(`/api/customers/${id}/crm-status`),
    enabled: !!id,
  });
}
